import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

export function BrandMark({ size = 36 }) {
  return (
    <div className="flex items-center gap-3 select-none">
      <img
        src="/logo.png"
        alt="Dapper Detailing Pros"
        style={{ width: size, height: size, borderRadius: 10, objectFit: "cover", background: "#000", boxShadow: "0 0 0 1px rgba(255,255,255,0.08), 0 10px 24px -12px rgba(255,59,48,0.35)" }}
      />
      <div className="leading-tight">
        <div className="font-heading font-bold text-white tracking-tight" style={{ fontSize: size * 0.5 }}>Dapper</div>
        <div className="text-overline" style={{ fontSize: 10 }}>Detailing Pros</div>
      </div>
    </div>
  );
}

export default function AppShell({ children, nav, title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static inset-y-0 left-0 z-40 w-72 border-r border-white/5 bg-[#0A0A0A] transition-transform flex flex-col`}
      >
        <div className="px-6 py-6 border-b border-white/5">
          <BrandMark size={36} />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-[#141414] text-white border border-white/10"
                    : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              {item.icon && <item.icon size={18} strokeWidth={1.75} />}
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg brand-surface">
            <div className="w-9 h-9 rounded-full bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center font-heading font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="nav-user-name">{user?.name}</div>
              <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white"
              data-testid="logout-button"
              aria-label="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 bg-black/60 z-30 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl">
          <div className="flex items-center gap-3 px-4 md:px-8 py-4 md:py-5">
            <button className="md:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="sidebar-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex-1 min-w-0">
              {subtitle && <div className="text-overline truncate">{subtitle}</div>}
              <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold tracking-tight truncate">{title}</h1>
            </div>
          </div>
        </header>
        <div className="flex-1 p-4 md:p-6 lg:p-10">{children}</div>
      </main>
    </div>
  );
}
